import { Injectable } from "@angular/core";
import {
  AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors,
  Validators
} from "@angular/forms";
import { UserGroupService } from "../services/user-group.service";
import { Post, PostReaction } from "../app/app.interfaces";
import { FormValidators } from "../form/form.validators";
import { UploadedFile } from "../app/app.models";
import { CategoryService } from '../services/category.service';
import { TagService } from '../services/tag.service';


export interface AllValidationErrors {
  control_name: string;
  error_name: string;
  error_value: any;
}

export interface FormGroupControls {
  [key: string]: AbstractControl;
}

@Injectable()
export class FormHelper {
  
  constructor(private formBuilder: FormBuilder,
              public userGroupService: UserGroupService,
              public categoryService: CategoryService,
              public tagService: TagService){

  }
  
  
  public buildPostForm(post?: Post): Promise<FormGroup> {
    
    if(post===undefined){
      post = {
        title: '',
        content: '',
        userGroups: [],
        categories: [],
        tags: [],
        attachments: [],
        anonymous: false
      }
    }
    
    return new Promise<FormGroup>(async (resolve) => {
      
			let attachmentsFormArray = new FormArray([]);
			
      let form = this.formBuilder.group({
        title: [post.title, Validators.compose([Validators.minLength(10), Validators.maxLength(100), Validators.required])],
        content: [post.content, Validators.compose([Validators.minLength(10), Validators.required])],
        anonymous: false,
        attachments: attachmentsFormArray
      });
      
      let attachmentControls = form.get('attachments') as FormArray;   
      if(post.attachments.length){ 
        for(let attachment of post.attachments){
          let newFormControl = new FormControl(UploadedFile.createFromUploadedFileInfo(attachment));
          attachmentControls.push(newFormControl);          
        }
      }      
      //Also add one empty
      let newFormControl = new FormControl();
      attachmentControls.push(newFormControl);     

      const userGroups = await this.userGroupService.allAsync();
      let userGroupCheckboxArray = [];
      for (let i in userGroups) {
        let userGroup = userGroups[i];
        let selected = post.userGroups.includes(userGroup['@id']);
        userGroupCheckboxArray.push(new FormControl(selected));
      }
      form.addControl('userGroups', new FormArray(userGroupCheckboxArray));
      form.controls.userGroups.setValidators(FormValidators.minEnabled(1));

      const categories = await this.categoryService.getAllCategories();
      let categoriesCheckboxArray = [];
      for (let i in categories) {
        let category = categories[i];
        let selected = post.categories.find(postCategory => postCategory.id === category.id);
        categoriesCheckboxArray.push(new FormControl(selected));
      }
      form.addControl('categories', new FormArray(categoriesCheckboxArray));

      const postPlainTags = TagService.makePlain(post.tags);
      form.addControl('tags', new FormControl(postPlainTags));
      form.controls.tags.setValidators(FormValidators.maxEnabled(3));


      resolve(form);

    });    
  }
  
  public buildPostReactionForm(postReaction?: PostReaction): Promise<FormGroup> {
    
    if(postReaction===undefined){
      postReaction = {
        content: '',
        attachments: [],
      }
    }
    
    return new Promise<FormGroup>((resolve) => {
      
      let attachmentsFormArray = new FormArray([]);
      
      let form = this.formBuilder.group({
			  content: [postReaction.content, Validators.compose([Validators.minLength(10), Validators.required])],
        attachments: attachmentsFormArray
      });
      
      let attachmentControls = form.get('attachments') as FormArray;   
      if(postReaction.attachments.length){ 
        for(let attachment of postReaction.attachments){
          let newFormControl = new FormControl(UploadedFile.createFromUploadedFileInfo(attachment));
          attachmentControls.push(newFormControl);          
        }
      }      
      //Also add one empty
      let newFormControl = new FormControl();
      attachmentControls.push(newFormControl);  
      
      
      resolve(form);
      
    });
    
    
    
  }

  public static createObjectFromFormValues(form: FormGroup, formArrayTransformableObjects: any = {}) {
    
    let newObject = {};
    
    for(let fieldName in form.controls) {
      let formControl = form.get(fieldName);
      let fieldValue;

      if (formControl instanceof FormArray && formArrayTransformableObjects.hasOwnProperty(fieldName)) {
        //We need to map this
        fieldValue = FormHelper.transformCheckboxArrayToSelectedObjectArray(formControl.value, formArrayTransformableObjects[fieldName]);
      }
      else if (formControl instanceof FormArray){
        fieldValue = formControl.value.filter(e => e);
      }
      else {
        fieldValue = formControl.value;
      }
      
      if(fieldValue!==null) newObject[fieldName] = fieldValue;
         
    }
    
    return newObject;  
        
  }
  
  public static updateObjectWithFormValues(object: any, form: FormGroup, formArrayTransformableObjects: any = {}){
        
    let updatedObject = Object.assign({}, object); // To circumvent passing by reference
        
    for(let fieldName in form.controls) {
      let formControl = form.get(fieldName);
      let fieldValue;

      if (formControl instanceof FormArray && formArrayTransformableObjects.hasOwnProperty(fieldName)) {
        //We need to map this
        fieldValue = FormHelper.transformCheckboxArrayToSelectedObjectArray(formControl.value, formArrayTransformableObjects[fieldName]);
      }
      else if (formControl instanceof FormArray){
        fieldValue = formControl.value.filter(e => e);
      }
      else {
        fieldValue = formControl.value;
      }
            
      if(object.hasOwnProperty(fieldName) && fieldValue!== null) {
        updatedObject[fieldName] = fieldValue;
      }      
    }
    
    return updatedObject;    
  }
  
  
  public static getInvalidFormControls(controls: FormGroupControls): string[] {
    let errors: string[] = [];
    Object.keys(controls).forEach(key => {
      const control = controls[ key ];
      if (control instanceof FormGroup) {
        errors = errors.concat(FormHelper.getInvalidFormControls(control.controls));
      }
      const controlErrors: ValidationErrors = controls[ key ].errors;
      if (controlErrors !== null) {
        Object.keys(controlErrors).forEach(keyError => {
          errors.push(key);
        });
      }
    });
    return errors;
  } 
  
  public static transformCheckboxArrayToSelectedObjectArray(checkboxArray: boolean[], objectArray: any[]) {
    
    let selectedObjects: any[] = [];

    for (let index in checkboxArray) {
      if (checkboxArray[index]) {
        selectedObjects.push(objectArray[index]);
      }
    }
    
    return selectedObjects;

  }
    
  public static getFormValidationErrors(controls: FormGroupControls): AllValidationErrors[] {
    let errors: AllValidationErrors[] = [];
    Object.keys(controls).forEach(key => {
      const control = controls[ key ];
      if (control instanceof FormGroup) {
        errors = errors.concat(FormHelper.getFormValidationErrors(control.controls));
      }
      const controlErrors: ValidationErrors = controls[ key ].errors;
      if (controlErrors !== null) {
        Object.keys(controlErrors).forEach(keyError => {
          errors.push({
            control_name: key,
            error_name: keyError,
            error_value: controlErrors[ keyError ]
          });
        });
      }
    });
    return errors;
  }

  
  
}



// WEBPACK FOOTER //
// ./src/helpers/form.helper.ts